import { createTheme, alpha, PaletteMode, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

// EmpireFX brand palette
export const brand = {
  50:  '#eeeeff',
  100: '#dddeff',
  200: '#c7c7fe',
  300: '#a5a5fb',
  400: '#7a7af7',
  500: '#5555f5',
  600: '#3636F2',
  700: '#2a2acc',
  800: '#1e1e9e',
  900: '#1a1a8c',
};

// Neutral palette mapped to DESIGN.md neutrals
export const gray = {
  50:  '#fafafb',
  100: '#f6f7f9',
  200: '#f1f3f6',
  300: '#ececf0',
  400: '#d8d9e0',
  500: '#9a9aa5',
  600: '#6b6b76',
  700: '#2c2c36',
  800: '#0a0a0f',
  900: '#0a0a0d',
};

export const green = {
  50:  '#f0fdf4',
  100: '#dcfce7',
  200: '#bbf7d0',
  300: '#86efac',
  400: '#4ade80',
  500: '#22c55e',
  600: '#16a34a',
  700: '#15803d',
  800: '#047857',
  900: '#14532d',
};

export const orange = {
  50:  '#fffbeb',
  100: '#fef3c7',
  200: '#fde68a',
  300: '#fcd34d',
  400: '#fbbf24',
  500: '#f59e0b',
  600: '#d97706',
  700: '#b45309',
  800: '#92400e',
  900: '#78350f',
};

export const red = {
  50:  '#fef2f2',
  100: '#fee2e2',
  200: '#fecaca',
  300: '#fca5a5',
  400: '#f87171',
  500: '#ef4444',
  600: '#dc2626',
  700: '#b91c1c',
  800: '#991b1b',
  900: '#7f1d1d',
};

export const getDesignTokens = (mode: PaletteMode) => {
  customShadows[1] =
    mode === 'dark'
      ? '0 1px 3px rgba(15,15,25,0.5), 0 1px 2px rgba(15,15,25,0.4)'
      : '0 1px 3px rgba(15,15,25,0.04), 0 1px 2px rgba(15,15,25,0.02)';

  return {
    palette: {
      mode,
      primary: {
        light: brand[300],
        main: brand[600],
        dark: brand[800],
        contrastText: '#ffffff',
        ...(mode === 'dark' && {
          light: brand[300],
          main: brand[500],
          dark: brand[700],
          contrastText: '#ffffff',
        }),
      },
      info: {
        light: '#bfdbfe',
        main: '#1e40af',
        dark: '#1e3a8a',
        contrastText: '#ffffff',
        ...(mode === 'dark' && {
          light: '#93c5fd',
          main: '#3b82f6',
          dark: '#1d4ed8',
          contrastText: '#ffffff',
        }),
      },
      warning: {
        light: orange[300],
        main: orange[800],
        dark: orange[900],
        ...(mode === 'dark' && {
          light: orange[400],
          main: orange[700],
          dark: orange[800],
        }),
      },
      error: {
        light: red[300],
        main: red[800],
        dark: red[900],
        ...(mode === 'dark' && {
          light: red[400],
          main: red[700],
          dark: red[800],
        }),
      },
      success: {
        light: green[300],
        main: green[800],
        dark: green[900],
        ...(mode === 'dark' && {
          light: green[400],
          main: green[600],
          dark: green[700],
        }),
      },
      grey: { ...gray },
      divider: mode === 'dark' ? '#23232b' : '#ececf0',
      background: {
        default: '#fafafb',
        paper: '#ffffff',
        ...(mode === 'dark' && { default: '#0a0a0d', paper: '#131318' }),
      },
      text: {
        primary: '#0a0a0f',
        secondary: '#6b6b76',
        ...(mode === 'dark' && { primary: '#f5f5f7', secondary: '#8a8a96' }),
      },
      action: {
        hover: alpha(gray[300], 0.4),
        selected: alpha(gray[300], 0.6),
        ...(mode === 'dark' && {
          hover: alpha('#23232b', 0.8),
          selected: alpha('#2e2e38', 0.8),
        }),
      },
    },
    typography: {
      fontFamily: "'Inter Tight', 'Inter', -apple-system, system-ui, sans-serif",
      h1: { fontSize: defaultTheme.typography.pxToRem(36), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.025em' },
      h2: { fontSize: defaultTheme.typography.pxToRem(28), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
      h3: { fontSize: defaultTheme.typography.pxToRem(22), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.015em' },
      h4: { fontSize: defaultTheme.typography.pxToRem(16), fontWeight: 600, lineHeight: 1.4 },
      h5: { fontSize: defaultTheme.typography.pxToRem(14), fontWeight: 600 },
      h6: { fontSize: defaultTheme.typography.pxToRem(13), fontWeight: 600 },
      subtitle1: { fontSize: defaultTheme.typography.pxToRem(14), fontWeight: 500 },
      subtitle2: { fontSize: defaultTheme.typography.pxToRem(13), fontWeight: 500 },
      body1: { fontSize: defaultTheme.typography.pxToRem(13) },
      body2: { fontSize: defaultTheme.typography.pxToRem(13), fontWeight: 400 },
      caption: { fontSize: defaultTheme.typography.pxToRem(11), fontWeight: 400 },
      overline: { fontSize: defaultTheme.typography.pxToRem(11), fontWeight: 500, letterSpacing: '0.08em' },
    },
    shape: {
      borderRadius: 14,
    },
    shadows: customShadows,
  };
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[300],
        main: brand[600],
        dark: brand[800],
        contrastText: '#ffffff',
      },
      info: {
        light: '#bfdbfe',
        main: '#1e40af',
        dark: '#1e3a8a',
        contrastText: '#ffffff',
      },
      warning: {
        light: orange[300],
        main: orange[800],
        dark: orange[900],
      },
      error: {
        light: red[300],
        main: red[800],
        dark: red[900],
      },
      success: {
        light: green[300],
        main: green[800],
        dark: green[900],
      },
      grey: { ...gray },
      divider: '#ececf0',
      background: {
        default: '#fafafb',
        paper: '#ffffff',
      },
      text: {
        primary: '#0a0a0f',
        secondary: '#6b6b76',
      },
      action: {
        hover: alpha(gray[300], 0.4),
        selected: alpha(gray[300], 0.6),
      },
      baseShadow: '0 1px 3px rgba(15,15,25,0.04), 0 1px 2px rgba(15,15,25,0.02)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: brand[300],
        main: brand[500],
        dark: brand[700],
        contrastText: '#ffffff',
      },
      info: {
        light: '#93c5fd',
        main: '#3b82f6',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
      },
      warning: {
        light: orange[400],
        main: orange[700],
        dark: orange[800],
      },
      error: {
        light: red[400],
        main: red[700],
        dark: red[800],
      },
      success: {
        light: green[400],
        main: green[600],
        dark: green[700],
      },
      grey: { ...gray },
      divider: '#23232b',
      background: {
        default: '#0a0a0d',
        paper: '#131318',
      },
      text: {
        primary: '#f5f5f7',
        secondary: '#8a8a96',
      },
      action: {
        hover: alpha('#23232b', 0.8),
        selected: alpha('#2e2e38', 0.8),
      },
      baseShadow: '0 1px 3px rgba(15,15,25,0.5), 0 1px 2px rgba(15,15,25,0.4)',
    },
  },
};

export const typography = {
  fontFamily: "'Inter Tight', 'Inter', -apple-system, system-ui, sans-serif",
  h1: { fontSize: defaultTheme.typography.pxToRem(36), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.025em' },
  h2: { fontSize: defaultTheme.typography.pxToRem(28), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
  h3: { fontSize: defaultTheme.typography.pxToRem(22), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.015em' },
  h4: { fontSize: defaultTheme.typography.pxToRem(16), fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: defaultTheme.typography.pxToRem(14), fontWeight: 600 },
  h6: { fontSize: defaultTheme.typography.pxToRem(13), fontWeight: 600 },
  subtitle1: { fontSize: defaultTheme.typography.pxToRem(14), fontWeight: 500 },
  subtitle2: { fontSize: defaultTheme.typography.pxToRem(13), fontWeight: 500 },
  body1: { fontSize: defaultTheme.typography.pxToRem(13) },
  body2: { fontSize: defaultTheme.typography.pxToRem(13), fontWeight: 400 },
  caption: { fontSize: defaultTheme.typography.pxToRem(11), fontWeight: 400 },
  overline: { fontSize: defaultTheme.typography.pxToRem(11), fontWeight: 500, letterSpacing: '0.08em' },
};

export const shape = {
  borderRadius: 14,
};

// @ts-ignore
const defaultShadows: Shadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;
